import React from 'react';
import { Slide } from '../components/deck/Slide';
import { Caption, Eyebrow, INK, Reveal, Title } from '../components/deck/Type';
type Tier = {
  name: string;
  price: string;
  unit: string;
  tone: string;
  flag?: string;
  items: string[];
};
const tiers: Tier[] = [{
  name: 'Free',
  price: '$0',
  unit: 'no billing',
  tone: '#8B95A8',
  items: ['1 workspace', 'Unlimited deals', '1 premium deal included', 'Pay-as-you-go premium add-ons']
}, {
  name: 'Plus',
  price: '$199',
  unit: 'billed monthly',
  tone: '#B9C2D2',
  items: ['1 workspace', 'Unlimited deals', '5 premium deals included', '250 MB storage per deal']
}, {
  name: 'Pro',
  price: '$499',
  unit: 'billed monthly',
  tone: INK.blue,
  flag: 'Most popular',
  items: ['3 workspaces', 'Unlimited deals', '10 premium deals included', '500 MB storage per deal', 'Priority support']
}, {
  name: 'Elite',
  price: '$999',
  unit: 'billed monthly',
  tone: INK.violet,
  items: ['5 workspaces', 'Unlimited deals', '25 premium deals included', '2 GB storage per deal', 'Dedicated account manager']
}, {
  name: 'Enterprise',
  price: 'From $10K',
  unit: 'per month',
  tone: INK.emerald,
  items: ['Unlimited workspaces', 'Bespoke DiligenceGPT™ deployment', 'Full integration with existing systems', 'Custom workflows and data feeds', 'Consulting and implementation services']
}];
const expansion = 'Additional workspaces · More premium analyses · Portfolio monitoring · LP intelligence and reporting · Enterprise data integrations · Organization-level intelligence models';
export function BusinessModelSlide() {
  return <Slide index={12} data-id="element-52" data-mp="2uzouee7hth1a">
      <div className="flex items-start justify-between gap-[3cqw]" data-id="element-53" data-mp="2zwyhvobbnimh">
        <div className="max-w-[58%]" data-id="element-54" data-mp="2zx89fgc0lxua">
          <Eyebrow data-id="element-55" data-mp="zufqh1heqh9g">Business model</Eyebrow>
          <Title className="mt-[2.2cqh]" data-id="element-56" data-mp="zvihrain6lpw">
            A recurring software model with a path to high-value enterprise
            contracts.
          </Title>
        </div>
        <div className="text-right" data-id="element-57" data-mp="19l1x4675bxg0">
          <Caption data-id="element-58" data-mp="19m3fkvqhrm7r">
            Prepay discounts: quarterly −10% · semi-annual −20% · annual −30%
          </Caption>
        </div>
      </div>

      <div className="mt-auto grid grid-cols-5 gap-[1.2cqw]" data-id="element-59" data-mp="1jig3te39p7v4">
        {tiers.map((tier, i) => <Reveal key={tier.name} delay={0.06 * i} className="relative flex h-full flex-col border border-white/10 bg-white/[0.03] p-[1.1cqw]" data-id="element-60" data-mp="1ejvewatgualz" data-mpi={i}>
            <div className="h-px w-full" style={{
          background: tier.tone
        }} data-id="element-61" data-mp="1nka1rem2vxrl" />
            <div className="mt-[1.6cqh] flex items-baseline justify-between gap-[0.4cqw]" data-id="element-62" data-mp="1ni72e3z1csa7">
              <span className="text-[clamp(8.5px,0.88cqw,18px)] uppercase tracking-[0.22em]" style={{
            color: tier.tone
          }} data-id="element-63" data-mp="3uazr6hrg30n2">
                {tier.name}
              </span>
              {tier.flag && <span className="text-[clamp(6.5px,0.68cqw,14px)] uppercase tracking-[0.16em] text-[#3B8CFF]" data-id="element-64" data-mp="29t8jbn13h0qf">
                  {tier.flag}
                </span>}
            </div>
            <p className="mt-[1.4cqh] text-[clamp(17px,2.05cqw,44px)] leading-none text-[#F4F7FC]" style={{
          fontFamily: 'var(--font-serif)'
        }} data-id="element-65" data-mp="202dr821636x2">
              {tier.price}
            </p>
            <p className="mt-[0.9cqh] text-[clamp(7.5px,0.78cqw,16px)] text-[#7C8699]" data-id="element-66" data-mp="1r30g74xeaaa8">
              {tier.unit}
            </p>
            <ul className="mt-[1.8cqh] space-y-[0.9cqh]" data-id="element-67" data-mp="1d4j09tdqe22v">
              {tier.items.map(item => <li key={item} className="flex gap-[0.5cqw] text-[clamp(9px,0.96cqw,20px)] leading-snug text-[#C3CBD9]" data-id="element-68" data-mp="1h64993n59w7s">
                  <span style={{
              color: tier.tone
            }} data-id="element-69" data-mp="3bhiyljc8ttn0">·</span>
                  <span data-id="element-70" data-mp="2mry4v0m9clqm">{item}</span>
                </li>)}
            </ul>
          </Reveal>)}
      </div>

      <Reveal delay={0.36} className="mt-[3cqh] grid grid-cols-[46%_1fr] gap-[3cqw] border-t border-white/12 pt-[2.2cqh]" data-id="element-71" data-mp="2htmv0pvyqb8e">
        <div data-id="element-72" data-mp="27u5qwcwd05ej">
          <span className="text-[clamp(8px,0.82cqw,17px)] uppercase tracking-[0.22em] text-[#D8A55A]" data-id="element-73" data-mp="27unl73zgo9jt">
            Enterprise &amp; strategic engagements
          </span>
          <Caption className="mt-[1.2cqh]" data-id="element-74" data-mp="23udh5wv8bt7l">
            Institutional deployments · Custom workflows · Data and system
            integrations · Strategic programs similar to the Sony engagement
          </Caption>
        </div>
        <div className="border-l border-white/10 pl-[2.4cqw]" data-id="element-75" data-mp="1p466w4fdlghz">
          <span className="text-[clamp(8px,0.82cqw,17px)] uppercase tracking-[0.22em] text-[#2FBE8F]" data-id="element-76" data-mp="1p1jr5hibzuoo">
            Expansion within an account
          </span>
          <Caption className="mt-[1.2cqh]" data-id="element-77" data-mp="1g412m35hay81">{expansion}</Caption>
        </div>
      </Reveal>
    </Slide>;
}